import React, { useState, useEffect, useRef } from "react";
import { CustomButton } from "@/components/ui/Buttons";
import Modal from "@/components/ui/Modal";
import {
  useGetMeQuery,
  useUpdateCreatorMutation,
} from "@/redux/feature/user/userApi";
import { useAppSelector } from "@/redux/hooks/hooks";
import DeleteIcon from "@/assets/icons/settingsModal/DeleteIconBg.svg";
import { X } from "lucide-react";
import { getInitials } from "@/utils/getFirstTwoLetter";
import { handleApiRequest } from "@/utils/handleApiRequest";
import toast from "react-hot-toast";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { TUser } from "@/types/types";

// Interface to define the structure of file details
interface FileDetails {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  webkitRelativePath: string;
  local_url: string;
  live_url: string | null;
}

const UserProfilePic = () => {
  // State variables to manage component functionality
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);

  // Ref to handle file input element
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadedPhoto = useRef<{ file: File; url: string } | null>(null);

  // Mutation hook to update user profile
  const [updateUser] = useUpdateCreatorMutation();

  // Get user data from Redux store and fetch user details
  const { user } = useAppSelector((state) => state.auth);
  const { data, refetch } = useGetMeQuery(user?.email);
  const userData: TUser | undefined = data?.data;

  // Generate user initials for fallback avatar
  const splitUserName = userData?.fullName
    ? getInitials(userData.fullName)
    : "";

  // Create object URL for selected file preview
  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    // Clean up object URL to prevent memory leaks
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection
  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) return;
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFile(null);
      return;
    }

    const file = e.target.files[0];
    e.target.value = "";
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      !file.size ||
      file.size > 5 * 1024 * 1024
    ) {
      toast.error("Select a JPG, PNG or WebP image up to 5 MB.");
      return;
    }
    uploadedPhoto.current = null;
    setSelectedFile(file);
  };

  // Remove selected file from preview
  const handleRemovePreview = () => {
    setSelectedFile(null);
    setPreview(undefined);
  };

  // Submit uploaded file to S3 and update user profile
  const handleSubmit = async () => {
    if (!selectedFile || isUploading) return;
    if (!userData?.user_id) {
      toast.error(
        "Your profile is still loading. Please try again in a moment.",
      );
      return;
    }

    setIsUploading(true);

    // Prepare file details for upload
    const fileDetails = {
      name: selectedFile.name,
      size: selectedFile.size,
      type: selectedFile.type,
      lastModified: selectedFile.lastModified,
      webkitRelativePath: selectedFile.webkitRelativePath,
      local_url: URL.createObjectURL(selectedFile),
      live_url: null,
      file: selectedFile,
    };

    try {
      // Upload file to S3
      let profilePictureLiveURL =
        uploadedPhoto.current?.file === selectedFile
          ? uploadedPhoto.current.url
          : null;
      if (!profilePictureLiveURL) {
        const uploaded = await UploadFileToS3(fileDetails);
        profilePictureLiveURL = uploaded.live_url as string;
        uploadedPhoto.current = {
          file: selectedFile,
          url: profilePictureLiveURL,
        };
      }

      // Update user profile with new profile picture
      await handleApiRequest({
        payload: {
          id: userData?.user_id,
          body: {
            profilePicture: profilePictureLiveURL,
          },
        },
        requestFunction: updateUser,
        onSuccess: () => {
          toast.success("Profile picture updated successfully!");
          setSelectedFile(null);
          refetch();
        },
        onError: (error) => {
          console.error("Profile picture update error:", error);
          toast.error(`Failed to update profile picture: ${error.message}`);
        },
      });
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to upload image. Please try again.",
      );
    } finally {
      URL.revokeObjectURL(fileDetails.local_url);
      setIsUploading(false);
    }
  };

  // Delete existing profile picture
  const handleDeleteAvatar = async () => {
    try {
      await handleApiRequest({
        payload: {
          id: userData?.user_id,
          body: {
            profilePicture: "", // Clear profile picture
          },
        },
        requestFunction: updateUser,
        onSuccess: () => {
          toast.success("Profile picture deleted successfully!");
          setIsModalOpen(false);
          setSelectedFile(null);
          setPreview(undefined);
          refetch();
        },
        onError: (error) => {
          console.error("Delete avatar error:", error);
          toast.error(`Failed to delete profile picture: ${error.message}`);
        },
      });
    } catch (error) {
      console.error("Delete avatar catch error:", error);
      toast.error("An unexpected error occurred while deleting the avatar.");
    }
  };

  // Render appropriate button based on component state
  const renderButton = () => {
    // Button state when uploading
    if (isUploading) {
      return (
        <CustomButton
          padding="8px 16px"
          fontSize="14px"
          fontWeight="500"
          border="1.5px solid #D0D5DD"
          variant="outline"
          color="#000000"
          className="leading-5 tracking-[-1.5%] shadow-md"
          disabled
        >
          Uploading...
        </CustomButton>
      );
    }

    // Button state when file is selected and ready to submit
    if (selectedFile) {
      return (
        <CustomButton
          padding="8px 16px"
          fontSize="14px"
          fontWeight="500"
          border="1.5px solid #D0D5DD"
          variant="outline"
          color="#000000"
          className="leading-5 tracking-[-1.5%] shadow-md"
          onClick={handleSubmit}
        >
          Submit
        </CustomButton>
      );
    }

    // Button state when existing profile picture is present
    if (userData?.profilePicture) {
      return (
        <CustomButton
          padding="8px 16px"
          fontSize="14px"
          fontWeight="500"
          border="1px solid #F9FAFB"
          color="#000000"
          backgroundColor="#F9FAFB"
          className="leading-5 tracking-[-1.5%]"
          onClick={() => setIsModalOpen(true)}
        >
          Delete
        </CustomButton>
      );
    }

    // Default button state for uploading
    return (
      <CustomButton
        padding="8px 16px"
        fontSize="14px"
        fontWeight="500"
        border="1.5px solid #D0D5DD"
        variant="outline"
        color="#000000"
        className="leading-5 tracking-[-1.5%] shadow-md"
        onClick={handleUploadClick}
      >
        Upload
      </CustomButton>
    );
  };

  return (
    <div className="flex flex-col md:flex-row items-center gap-y-5 gap-x-4 ">
      {/* Render profile picture or initials */}
      {userData?.profilePicture || preview ? (
        <div className="relative">
          <img
            className="w-[72px] h-[72px] rounded-full object-cover"
            src={preview || userData?.profilePicture}
            alt="profile"
          />
          {/* Cross icon to remove preview */}
          {(selectedFile || userData?.profilePicture) && (
            <button
              onClick={
                selectedFile ? handleRemovePreview : () => setIsModalOpen(true)
              }
              disabled={isUploading}
              className="absolute top-0 right-0 bg-gray-200 rounded-full p-1 hover:bg-gray-300 transition-colors"
            >
              <X size={16} className="text-gray-600" />
            </button>
          )}
        </div>
      ) : (
        // Fallback to initials if no profile picture
        <div className="size-[72px] flex justify-center items-center text-[#17457D] rounded-full text-lg font-semibold leading-5 bg-[#17457D0F]">
          {splitUserName}
        </div>
      )}
      <div className="flex gap-4 items-center">
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={onSelectFile}
          accept="image/jpeg,image/png,image/webp"
          disabled={isUploading}
          className="hidden"
        />
        {renderButton()}
      </div>
      {/* Modal for confirming avatar deletion */}
      <Modal padding="24px" isOpen={isModalOpen} setOpen={setIsModalOpen}>
        <DeleteAvatarModal
          setIsModalOpen={setIsModalOpen}
          onConfirmDelete={handleDeleteAvatar}
        />
      </Modal>
    </div>
  );
};

// Modal component for confirming avatar deletion
const DeleteAvatarModal = ({
  setIsModalOpen,
  onConfirmDelete,
}: {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirmDelete: () => void;
}) => {
  return (
    <div className="flex flex-col justify-center items-center">
      <DeleteIcon />
      <div className="space-y-2 mb-8 text-center">
        <h6 className="text-[#101828] text-lg leading-5 font-semibold mt-7">
          Delete Avatar
        </h6>
        <p className="text-[#475467] text-center text-sm leading-5">
          Are you sure you want to delete the avatar
        </p>
      </div>
      <div className="w-full flex gap-3">
        <CustomButton
          onClick={() => setIsModalOpen(false)}
          variant="outline"
          fontWeight="600"
          padding="8px"
          fontSize="16px"
          color="#344054"
          border="1px solid #D0D5DD"
          backgroundColor="#FFFFFF"
          className="w-full leading-6"
        >
          Cancel
        </CustomButton>
        <CustomButton
          onClick={onConfirmDelete}
          fontWeight="600"
          padding="8px"
          fontSize="16px"
          color="#FFFFFF"
          backgroundColor="#17457D"
          className="w-full"
        >
          Confirm
        </CustomButton>
      </div>
    </div>
  );
};

export default UserProfilePic;
